import React, { useRef } from 'react';
import cn from 'classnames';
import { Button, Form, Grid, Input, Select, View, ViewItem } from 'src/components/common';
import './styles.scss';
import { IRootState } from 'src/redux/rootReducer';
import { connect } from 'react-redux';
import { EmployeeFormType, EmployeeInfo, EmployeeModalType } from 'src/redux/employees/types';
import { GrClose } from 'react-icons/gr';
import _ from 'lodash';
import { ErrorService, Yup } from 'src/services';
import { Formik, FormikProps } from 'formik';
import { addEmployeeAsync, setOpenEmployeeModal, updateEmployeeAsync } from 'src/redux/employees/actions';

import { CustomModal } from 'src/components/Modal';
import { LockupValueByLabel } from 'src/utils';

export const ModalTypes = {
  VIEW: 'VIEW',
  EDIT: 'EDIT',
  ADD: 'ADD',
};

const EmployeeSchema = Yup.object().shape({
  employeeId: Yup.string()
    .test(
      'len',
      ErrorService.MESSAGES.invalidEmployeeID,
      val => (val?.toString()?.length || 0) === 8 && !val.match(/[^\w]|_/g),
    )
    .required(),
  lastName: Yup.string().max(50, 'Last Name must be at most 50 characters').required(),
  firstName: Yup.string().max(50, 'First Name must be at most 50 characters').required(),
  tradeId: Yup.number().nullable().required(),
  rate: Yup.number().typeError('Rate is a decimal number').min(0, 'Rate must be greater than or equal to 0').required(),
  levelId: Yup.number().nullable().required(),
  status: Yup.string().typeError(ErrorService.MESSAGES.required).required(),
});

const EmployeesModal: React.FC<Props> = ({
  loading,
  selectedEmployee: data,
  openModalType,
  onClose,
  onEdit,
  onUpdateEmployee,
  onAddEmployee,
  onSetOpenModal,
  levels,
  trades,
  status,
}) => {
  const formRef = useRef<FormikProps<EmployeeFormType>>(null);

  const convertInfoToForm = (value: EmployeeInfo) => {
    let result = { ...value };
    delete result.trade;
    delete result.level;
    return {
      ...result,
      tradeId: LockupValueByLabel(value.trade, trades),
      levelId: LockupValueByLabel(value.level, levels),
    };
  };

  const handleCloseEdit = () => {
    if (openModalType === 'EDIT') {
      onSetOpenModal('VIEW');
    } else {
      onSetOpenModal(false);
    }
  };
  const handleSubmit = (value: EmployeeFormType) => {
    if (openModalType === ModalTypes.EDIT) onUpdateEmployee(value);
    if (openModalType === ModalTypes.ADD) onAddEmployee(value);
  };

  const renderTitle = () => {
    switch (openModalType) {
      case ModalTypes.VIEW:
        return [
          <View
            key={'close-button'}
            onClick={onClose}
            className={cn('ctn-employees-modal__title__button', 'mb-5')}
            isRow
            align={'center'}>
            <GrClose /> <h4>CLOSE</h4>
          </View>,
          <View key={'close-title'} flexGrow={1} justify={'space-between'} isRow align={'center'}>
            <h3>Employee Details</h3>
            <Button label={'EDIT'} onClick={onEdit} />
          </View>,
        ];
      case ModalTypes.EDIT:
        return <h3>Edit Employee Details</h3>;
      case ModalTypes.ADD:
        return <h3>Add New Employee</h3>;
    }
  };

  const renderBody = modalType => {
    if (modalType === ModalTypes.EDIT || modalType === ModalTypes.ADD)
      return (
        <Formik
          key={`employee-modal-${modalType}`}
          initialValues={convertInfoToForm(data) as EmployeeFormType}
          onSubmit={handleSubmit}
          validationSchema={EmployeeSchema}
          innerRef={formRef}>
          {({ handleSubmit, errors, touched, getFieldProps, setFieldValue, setFieldTouched }) => (
            <Form preventDefault autoComplete="off" className="ctn-uam__form">
              <Grid.Wrap>
                <Grid.Item>
                  <Input
                    label="Employee ID *"
                    placeholder="Employee ID"
                    errorMessage={touched.employeeId ? errors.employeeId : ''}
                    {...getFieldProps('employeeId')}
                  />
                </Grid.Item>
                <Grid.Item />
                <Grid.Item>
                  <Input
                    label="Last Name *"
                    placeholder="Last Name"
                    errorMessage={touched.lastName ? errors.lastName : ''}
                    {...getFieldProps('lastName')}
                  />
                </Grid.Item>
                <Grid.Item>
                  <Input
                    label="First Name *"
                    placeholder="First Name"
                    errorMessage={touched.firstName ? errors.firstName : ''}
                    {...getFieldProps('firstName')}
                  />
                </Grid.Item>
                <Grid.Item>
                  <Select
                    creatable={false}
                    options={trades || []}
                    label="Trade *"
                    placeholder="Choose Here ..."
                    errorMessage={touched.tradeId ? errors.tradeId : ''}
                    {...getFieldProps('tradeId')}
                    name="tradeId"
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                  />
                </Grid.Item>
                <Grid.Item>
                  <Select
                    creatable={false}
                    options={levels || []}
                    label="Level *"
                    placeholder="Choose Here ..."
                    errorMessage={touched.levelId ? errors.levelId : ''}
                    {...getFieldProps('levelId')}
                    name="levelId"
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                  />
                </Grid.Item>
                <Grid.Item>
                  <Input
                    label="Rate *"
                    placeholder="Rate"
                    errorMessage={touched.rate ? errors.rate : ''}
                    {...getFieldProps('rate')}
                    name={'rate'}
                    onChange={e => {
                      const value = e.currentTarget.value;
                      const name = e.currentTarget.name;
                      setFieldValue(name, value.replace(/(\.\d\d)(\d+)/, '$1'));
                    }}
                  />
                </Grid.Item>
                <Grid.Item>
                  <Select
                    creatable={false}
                    options={status || []}
                    label="Status *"
                    placeholder="Choose Here ..."
                    errorMessage={touched.status ? errors.status : ''}
                    {...getFieldProps('status')}
                    name="status"
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                  />
                </Grid.Item>
              </Grid.Wrap>
              <View className={cn('ctn-employees-modal__footer')}>
                <View flexGrow={1} justify={'flex-end'} isRow className={'mt-32'}>
                  <Button
                    label={'CANCEL'}
                    onClick={handleCloseEdit}
                    className={cn('mr-5')}
                    variant={'outline'}
                    type={'button'}
                  />
                  <Button type={'button'} onClick={() => handleSubmit()} isLoading={loading}>
                    {openModalType === ModalTypes.ADD ? 'ADD' : 'UPDATE'}
                  </Button>
                </View>
              </View>
            </Form>
          )}
        </Formik>
      );
    return data ? (
      <View className={cn('ctn-employees-modal__content', 'mb-12')} flexGrow={1}>
        <Grid.Wrap>
          <ViewItem label={'Employee ID'} value={data.employeeId} />
        </Grid.Wrap>
        <Grid.Wrap>
          <ViewItem label={'Last Name'} value={data.lastName} />
          <ViewItem label={'First Name'} value={data.firstName} />
        </Grid.Wrap>
        <Grid.Wrap>
          <ViewItem label={'Trade'} value={data.trade} />
          <ViewItem label={'Level'} value={data.level} />
        </Grid.Wrap>{' '}
        <Grid.Wrap>
          <ViewItem label={'Rate'} value={parseFloat(data.rate).toFixed(2)} />
          <ViewItem
            label={'Status'}
            value={
              <span
                className={cn(
                  { 'active-status': data.status === 'active' },
                  { 'inactive-status': data.status === 'inactive' },
                )}>
                {_.capitalize(data.status)}
              </span>
            }
          />
        </Grid.Wrap>
      </View>
    ) : null;
  };

  return (
    <CustomModal
      containerClassName="ctn-employees-modal"
      openModalType={openModalType}
      header={renderTitle()}
      body={renderBody(openModalType)}
    />
  );
};

type Props = ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps & {
    openModalType: EmployeeModalType;
    onClose?: () => void;
    onEdit?: () => void;
  };

const mapStateToProps = (state: IRootState) => ({
  loading: state.employees.isLoading,
  trades: state.content.trades,
  levels: state.content.levels,
  status: state.content.status,
  selectedEmployee: state.employees.selectedEmployee,
});

const mapDispatchToProps = {
  onUpdateEmployee: updateEmployeeAsync.request,
  onAddEmployee: addEmployeeAsync.request,
  onSetOpenModal: setOpenEmployeeModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(EmployeesModal);
